import type {
  NodePath,
  TreeConditionNode,
} from '@/components/tree-dnd/tree-dnd-types';
import { BoughtProductCondition } from '@/components/tree-dnd/tree-dnd-conditions/bought-product-condition';
import { ClickedCampaignCondition } from '@/components/tree-dnd/tree-dnd-conditions/clicked-campaign-condition';
import { ConditionEditSheetConfigProvider } from '@/components/tree-dnd/tree-dnd-conditions/condition-edit-sheet-context';
import { HasNotificationsEnabledCondition } from '@/components/tree-dnd/tree-dnd-conditions/has-notifications-enabled-condition';
import { InstalledAppCondition } from '@/components/tree-dnd/tree-dnd-conditions/installed-app-condition';
import { LastAppAccessAtCondition } from '@/components/tree-dnd/tree-dnd-conditions/last-app-access-at-condition';
import { LastNotificationAtCondition } from '@/components/tree-dnd/tree-dnd-conditions/last-notification-at-condition';
import { LastOrderCondition } from '@/components/tree-dnd/tree-dnd-conditions/last-order-condition';
import { LocationCondition } from '@/components/tree-dnd/tree-dnd-conditions/location-condition';
import { OrderFrequencyCondition } from '@/components/tree-dnd/tree-dnd-conditions/order-frequency-condition';
import { ReceivedNotificationCondition } from '@/components/tree-dnd/tree-dnd-conditions/received-notification-condition';
import { RfmGroupCondition } from '@/components/tree-dnd/tree-dnd-conditions/rfm-group-condition';
import { TotalSpendCondition } from '@/components/tree-dnd/tree-dnd-conditions/total-spend-condition';
import { ViewedProductCondition } from '@/components/tree-dnd/tree-dnd-conditions/viewed-product-condition';
import { useTreeDndStore } from '@/components/tree-dnd/tree-dnd-store';

interface ConditionRendererProps {
  path: NodePath;
  node: TreeConditionNode;
  showAddAnother?: boolean;
  showMoreOptions?: boolean;
}

export function ConditionRenderer({
  path,
  node,
  showAddAnother = true,
  showMoreOptions = true,
}: ConditionRendererProps) {
  const updateCondition = useTreeDndStore((state) => state.updateCondition);
  const parentPath = path.slice(0, -1);

  const content = (() => {
    switch (node.kind) {
      case 'installedApp':
        return (
          <InstalledAppCondition
            node={node}
            onChange={(args) => updateCondition(path, { args })}
          />
        );
      case 'boughtProduct':
        return (
          <BoughtProductCondition
            node={node}
            onChange={(args) => updateCondition(path, { args })}
          />
        );
      case 'receivedNotification':
        return (
          <ReceivedNotificationCondition
            node={node}
            onChange={(args) => updateCondition(path, { args })}
          />
        );
      case 'clickedCampaign':
        return (
          <ClickedCampaignCondition
            node={node}
            onChange={(args) => updateCondition(path, { args })}
          />
        );
      case 'rfmGroup':
        return (
          <RfmGroupCondition
            node={node}
            onChange={(args) => updateCondition(path, { args })}
          />
        );
      case 'lastNotificationAt':
        return (
          <LastNotificationAtCondition
            node={node}
            onChange={(args) => updateCondition(path, { args })}
          />
        );
      case 'lastAppAccessAt':
        return (
          <LastAppAccessAtCondition
            node={node}
            onChange={(args) => updateCondition(path, { args })}
          />
        );
      case 'viewedProduct':
        return (
          <ViewedProductCondition
            node={node}
            onChange={(args) => updateCondition(path, { args })}
          />
        );
      case 'location':
        return (
          <LocationCondition
            node={node}
            onChange={(args) => updateCondition(path, { args })}
          />
        );
      case 'lastOrder':
        return (
          <LastOrderCondition
            node={node}
            onChange={(args) => updateCondition(path, { args })}
          />
        );
      case 'orderFrequency':
        return (
          <OrderFrequencyCondition
            node={node}
            onChange={(args) => updateCondition(path, { args })}
          />
        );
      case 'totalSpend':
        return (
          <TotalSpendCondition
            node={node}
            onChange={(args) => updateCondition(path, { args })}
          />
        );
      case 'hasNotificationsEnabled':
        return (
          <HasNotificationsEnabledCondition
            node={node}
            onChange={(args) => updateCondition(path, { args })}
          />
        );
    }
  })();

  return (
    <ConditionEditSheetConfigProvider
      path={path}
      parentPath={parentPath}
      showAddAnother={showAddAnother}
      showMoreOptions={showMoreOptions}
    >
      {content}
    </ConditionEditSheetConfigProvider>
  );
}
